/* eslint-disable react-hooks/exhaustive-deps */
import { Box, InputAdornment, Link, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '../../contexts/globalContext';
import api from '../../api/api';
import { PossibleMatches } from './models/PossibleMatches';
import MatchesSearchInformation from './components/MatchesSearchInformation/MatchesSearchInformation';
import _ from 'lodash';
import { i18n } from '../../i18n/i18n';
import MatchesSection from './components/MatchesSection/MatchesSection';
import constants, {
    ANALYSIS_TYPE,
    COLORS,
    MAP_PLATFORM_TO_SEARCH_TYPE,
    socialPlatformsMap,
    STATUS,
} from '../../constants/constants';
import WebMentions from '../../../investigate/Content/routes/report/components/WebMentions/WebMentions';
import moment from 'moment';
import { getPollingCtrl } from '../../contexts/pollingController';
import { createInfoBlocks, formatDateOfBirth } from '../../utils/utils';
import PossibleMatchesTitleSkeleton from './components/PossibleMatchesTitleSkeleton/PossibleMatchesTitleSkeleton';
import MatchesActionBar from './components/MatchesActionBar/MatchesActionBar';
import fallbackUserSquare from '../../images/fallbacks/user-fallback-square.svg';
import {
    IconPossibleMatches,
    IconSearch,
    IconXWindow,
    IconArrowBack,
    IconNoCollectionsFound,
} from '../../../common/images';
import { SocialCard, CustomInfoCard, CustomList, CustomAvatar, MuiDialogWrapper } from '../../components';
import { useQueryClient } from 'react-query';
import { capitalizeFirstLetter } from '../../utils/formatters';
import { obj } from '../../models/common-types';
import { isSuccess } from '../../utils/communication';
import { ElementsDivider, NOTIFICATION, SmartImage, useNotifications } from '@cellebrite/saas-ui-common';
import { PENDO_EVENTS, pendoTrack } from '../../utils/pendo';
import { InfoIcon, RunAsANewSearchIcon, TuneIcon, MysteryIcon } from '@cellebrite/saas-ui-icons';
import useDialogStorageActions from './useDialogStorageActions';

interface PossibleMatchesProps {
    close: () => void;
    data: any;
}

const PossibleMatchesDialog: React.FC<PossibleMatchesProps> = ({ close, data }) => {
    const [showMessage] = useNotifications();
    const { casesData, workspacesData, collectionData, adminData } = useContext(GlobalContext);
    const [possibleMatches, setPossibleMatches] = useState(new PossibleMatches());
    const possibleMatchesPollingIntervalRef = useRef(null);
    const [searchInput, setSearchInput] = useState('');
    const [displayedAccounts, setDisplayedAccounts] = useState(true);
    const [filterByPlatform, setFilterByPlatform] = useState(null);
    const [clickedAccount, setClickedAccount] = useState(null);
    const [infoBlocks, setInfoBlocks] = useState(null);
    const [selectedByPlatform, setSelectedByPlatform]: any = useState({});
    const { selectedIds, addItemWithExpiry, isButtonDisabled } = useDialogStorageActions({
        caseId: `adding_accounts-${data.id}`,
        selectedByPlatform,
        clickedAccountId: clickedAccount ? clickedAccount?.id : null,
    });

    const queryClient = useQueryClient();

    const filteredPersonPictures = possibleMatches.overview?.profilePictures.filter(
        (profilePicture) => profilePicture?.value?.imgLocalSrc,
    );
    const webMentionsFiltered = possibleMatches.webMentions?.filter(
        (webMention) =>
            webMention.searchResultTitle?.toLowerCase().includes(searchInput.toLowerCase()) ||
            webMention.searchResultSnippet?.toLowerCase().includes(searchInput.toLowerCase()) ||
            webMention.url?.toLowerCase().includes(searchInput.toLowerCase()),
    );

    const getDobFromAccount = (account) =>
        account.overview?.dob?.length ? moment(account.overview.dob[0].value.start).format('LL') : null;

    const getInfoLine = (account) => {
        const result = [];
        if (account.overview?.dob?.length) {
            result.push(
                <Box sx={{ display: 'flex' }}>
                    <Typography variant="s14w500" className="color-md">
                        {i18n.translate('Date of Birth')}:
                    </Typography>
                    <Typography variant="s14w500" className="color-bd" sx={{ ml: 0.5 }}>
                        {getDobFromAccount(account)}
                    </Typography>
                </Box>,
            );
        }
        if (account.location) {
            result.push(
                <Box sx={{ display: 'flex' }}>
                    <Typography variant="s14w500" className="color-md">
                        {i18n.translate('Location')}:
                    </Typography>
                    <Typography variant="s14w500" className="color-bd" sx={{ ml: 0.5 }}>
                        {account.location}
                    </Typography>
                </Box>,
            );
        }
        return result;
    };

    function getOrderByPlatform(platform) {
        let result = 0;
        switch (platform) {
            case 'PIPL':
                result = 14;
                break;
            case 'FACEBOOK':
                result = 13;
                break;
            case 'INSTAGRAM':
                result = 12;
                break;
            case 'LINKEDIN':
                result = 11;
                break;
            case 'TWITTER':
                result = 10;
                break;
            case 'YOUTUBE':
                result = 9;
                break;
            case 'TIKTOK':
                result = 8;
                break;
            case 'SNAPCHAT':
                result = 7;
                break;
            case 'TWITCH':
                result = 6;
                break;
            case 'PINTEREST':
                result = 5;
                break;
            case 'ThatsThem':
                result = 4;
                break;
            case 'CASHAPP':
                result = 3;
                break;
            case 'VKONTAKTE':
                result = 2;
                break;
            case 'XING':
                result = 1;
                break;
        }
        return result;
    }

    const getSections = () => {
        const result = [];
        const sections = [];
        const advancedSections = [];
        let isDisplayedAccounts = false;
        _.forOwn(possibleMatches.accounts, (value, key) => {
            if (value.length && (!filterByPlatform || key.toLowerCase() === filterByPlatform)) {
                const arrayValueIncluded = (array, input) => {
                    let valueIncluded = false;
                    array.forEach((value) => {
                        if (value?.toLowerCase().includes(input.toLowerCase())) {
                            valueIncluded = true;
                        }
                    });
                    return valueIncluded;
                };
                const filteredAccounts = value.filter((account) => {
                    let result;
                    if (['PIPL', 'LEXISNEXIS', 'ThatsThem'].includes(key)) {
                        const dobs = account.overview?.dob
                            ?.filter((dob) => dob.value?.start)
                            .map((dob) => formatDateOfBirth(dob.value));
                        const usernames = account.overview?.usernames
                            ?.filter((username) => username.value?.userName)
                            .map((username) => username.value.userName);
                        const locations = account.overview?.locations
                            ?.filter((location) => location.value)
                            .map((location) => location.value?.address);
                        const emails = account.overview?.emails
                            ?.filter((email) => email.value)
                            .map((email) => email.value);
                        const phones = account.overview?.phones
                            ?.filter((phone) => phone.value?.countryCode)
                            .map((phone) => `${phone.value.countryCode}${phone.value.phone}`);
                        result =
                            account.name?.toLowerCase().includes(searchInput.toLowerCase()) ||
                            account.overview?.gender?.value.toLowerCase().includes(searchInput.toLowerCase()) ||
                            arrayValueIncluded(usernames, searchInput) ||
                            arrayValueIncluded(locations, searchInput) ||
                            arrayValueIncluded(emails, searchInput) ||
                            arrayValueIncluded(phones, searchInput) ||
                            arrayValueIncluded(dobs, searchInput);
                    } else {
                        result =
                            account.name?.toLowerCase().includes(searchInput.toLowerCase()) ||
                            account.userName?.toLowerCase().includes(searchInput.toLowerCase()) ||
                            account.location?.toLowerCase().includes(searchInput.toLowerCase()) ||
                            account.description?.toLowerCase().includes(searchInput.toLowerCase()) ||
                            account.workplace?.toLowerCase().includes(searchInput.toLowerCase()) ||
                            account.phone?.toLowerCase().includes(searchInput.toLowerCase());
                    }
                    return result;
                });

                if (filteredAccounts.length) {
                    isDisplayedAccounts = true;
                }
                (possibleMatches.filters[key] ? advancedSections : sections).push({
                    accounts: filteredAccounts,
                    platform: key,
                    fallback: possibleMatches.fallbacks[key],
                    element: (
                        <MatchesSection
                            key={key}
                            searchInformation={possibleMatches.searchInformation}
                            findInput={searchInput}
                            filters={possibleMatches.filters[key]}
                            fallback={possibleMatches.fallbacks[key]}
                            platform={key.toLowerCase()}
                            filterByPlatform={filterByPlatform}
                            setFilterByPlatform={setFilterByPlatform}
                            accounts={filteredAccounts}
                            onCheckedChange={(platform, checked, account) => {
                                if (checked) {
                                    if (!selectedByPlatform[platform]) {
                                        selectedByPlatform[platform] = [];
                                    }
                                    selectedByPlatform[platform].push(account);
                                } else {
                                    selectedByPlatform[platform] = _.reject(
                                        selectedByPlatform[platform],
                                        function (checkedPart) {
                                            return checkedPart.id === account.id;
                                        },
                                    );
                                    if (!selectedByPlatform[platform].length) {
                                        delete selectedByPlatform[platform];
                                    }
                                }
                                possibleMatches.updateCheckedAccount(account, checked);
                                setSelectedByPlatform(_.cloneDeep(selectedByPlatform));
                                setPossibleMatches(_.cloneDeep(possibleMatches));
                            }}
                            setClickedAccount={setClickedAccount}
                        />
                    ),
                });
            }
        });
        if (advancedSections.length) {
            const isSectionsAccountExist = sections
                .map((section) => section.accounts)
                .filter((accounts) => accounts.length).length;
            const isAdvancedSectionsAccountExist = advancedSections
                .map((advancedSection) => advancedSection.accounts)
                .filter((accounts) => accounts.length).length;
            if (isAdvancedSectionsAccountExist) {
                result.push(
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TuneIcon style={{ fill: COLORS.BRAND_ACTIVE }} />
                            <Typography variant="s16w700" sx={{ color: COLORS.BRAND_ACTIVE, ml: 1 }}>
                                {i18n.translate('Advanced Search Matches')}
                            </Typography>
                        </Box>
                        <Typography variant="s14w500" sx={{ color: COLORS.MUTED_DARK, ml: 4 }}>
                            {i18n.translate('Based on some or all or your additional search terms.')}
                        </Typography>
                    </Box>,
                );

                _.orderBy(
                    advancedSections,
                    [
                        (advancedSection) => Boolean(advancedSection.fallback),
                        (advancedSection) => {
                            return getOrderByPlatform(advancedSection.platform);
                        },
                    ],
                    ['desc', 'desc'],
                ).forEach((advancedSection) => {
                    if (advancedSection.accounts.length) {
                        result.push(advancedSection.element);
                    }
                });
            }
            if (isSectionsAccountExist) {
                result.push(
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 2,
                            mt: isAdvancedSectionsAccountExist ? 2 : 0,
                        }}
                    >
                        <MysteryIcon style={{ fill: COLORS.BRAND_DARK }} />
                        <Typography variant="s16w700" sx={{ color: COLORS.MUTED_HEAVY, ml: 1 }}>
                            {i18n.translate('Name Search Matches')}
                        </Typography>
                    </Box>,
                );
                _.orderBy(
                    sections,
                    [
                        (section) => Boolean(section.fallback),
                        (section) => {
                            return getOrderByPlatform(section.platform);
                        },
                    ],
                    ['desc', 'desc'],
                ).forEach((section) => {
                    if (section.accounts.length) {
                        result.push(section.element);
                    }
                });
            }
        } else {
            _.orderBy(
                sections,
                [
                    (section) => Boolean(section.fallback),
                    (section) => {
                        return getOrderByPlatform(section.platform);
                    },
                ],
                ['desc', 'desc'],
            ).forEach((section) => {
                result.push(section.element);
            });
        }
        if (displayedAccounts !== isDisplayedAccounts) {
            setDisplayedAccounts(isDisplayedAccounts);
        }
        return result;
    };

    const formatDisplayName = (platform: string, account: obj<any>) => {
        let displayName = null;

        if (['thatsthem', 'pipl'].includes(platform)) {
            displayName = account.name
                ? `${account.name} (${account.id.slice(0, 6)})`
                : account.userName
                  ? `${account.userName} (${account.id.slice(0, 6)})`
                  : account.id.slice(0, 6);
        } else if (platform === 'lexisnexis') {
            displayName = account.name ? `${account.name} (${account.id})` : account.id;
        }

        return displayName;
    };

    const showPersonOfInterest = !!filteredPersonPictures?.length;

    const fetchPossibleMatches = async () => {
        const rawData = await api.collections.fetchPossibleMatches(
            workspacesData.activeWorkspace.id,
            casesData.activeCaseId,
            data.id,
        );
        possibleMatches.update(rawData);
        setPossibleMatches(_.cloneDeep(possibleMatches));
    };

    useEffect(() => {
        if (possibleMatchesPollingIntervalRef.current) {
            clearInterval(possibleMatchesPollingIntervalRef.current);
        }
        possibleMatchesPollingIntervalRef.current = setInterval(() => {
            if (possibleMatches.searchInformation?.status === STATUS.IN_PROGRESS) {
                fetchPossibleMatches();
            } else {
                clearInterval(possibleMatchesPollingIntervalRef.current);
            }
        }, constants.POSSIBLE_MATCHES_POLLING_DELAY);
    }, [possibleMatches]);

    useEffect(() => {
        let isSelected = false;
        _.forOwn(selectedByPlatform, (accounts, platform) => {
            if (accounts.length) {
                isSelected = true;
            }
        });
    }, [selectedByPlatform]);

    useEffect(() => {
        if (clickedAccount) {
            setInfoBlocks(
                createInfoBlocks(
                    'POSSIBLE_MATCHES',
                    clickedAccount.overview,
                    collectionData.createCollection.mutate,
                    collectionData.addToReport,
                    data.id,
                    adminData.isQuotaLimitReached,
                ),
            );
        }
    }, [clickedAccount]);

    useEffect(() => {
        fetchPossibleMatches();
        const pollingCtrl = getPollingCtrl();
        pollingCtrl.setStopCasePollingInfo(true);
        pollingCtrl.setStopCollectionPollingInfo(true);
        return () => {
            clearInterval(possibleMatchesPollingIntervalRef.current);
            const pollingCtrl = getPollingCtrl();
            pollingCtrl.setStopCasePollingInfo(false);
            pollingCtrl.setStopCollectionPollingInfo(false);
            queryClient.invalidateQueries('cases');
            queryClient.invalidateQueries('collections');
        };
    }, []);

    return (
        <MuiDialogWrapper
            contentStyles={{ pt: clickedAccount ? null : 0 }}
            titleStyles={{ pr: clickedAccount ? null : 0 }}
            styles={data.styles}
            onClose={() => {
                close();
                data.onClose?.();
            }}
            buttons={
                clickedAccount
                    ? [
                          {
                              startIcon: <RunAsANewSearchIcon />,
                              name: i18n.translate('Create a New Search'),
                              disabled: adminData.isQuotaLimitReached || clickedAccount.isThatsThemSource,
                              onClick: async () => {
                                  const promises = [];
                                  const tempAccounts = [];
                                  const platform = clickedAccount.type
                                      ? clickedAccount.type.split(' ')?.[0].toLowerCase()
                                      : 'pipl';
                                  tempAccounts.push(clickedAccount);
                                  let displayName = formatDisplayName(platform, clickedAccount);

                                  promises.push(
                                      collectionData.createCollection.mutate({
                                          pendoEventName: PENDO_EVENTS.SEARCH_POSSIBLE_MATCHES_ACCOUNT,
                                          newSearch: {
                                              hideMessage: true,
                                              searchTerm:
                                                  platform === 'pipl'
                                                      ? clickedAccount.id
                                                      : clickedAccount.userName || clickedAccount.id,
                                              searchType: socialPlatformsMap.get(capitalizeFirstLetter(platform)),
                                              analysisType: clickedAccount.type
                                                  ? ANALYSIS_TYPE.SOCIAL
                                                  : ANALYSIS_TYPE.FULL,
                                              ...(displayName && { displayName }),
                                          },
                                      }),
                                  );
                              },
                              color: 'secondary',
                              styles: { minWidth: '171px !important', height: 32 },
                          },
                          {
                              startIcon: <IconSearch />,
                              disabled: clickedAccount.added || isButtonDisabled,
                              name: i18n.translate('Add to this Search'),
                              onClick: async () => {
                                  let platform = clickedAccount.type
                                      ? clickedAccount.type.split(' ')?.[0].toLowerCase()
                                      : 'pipl';
                                  // payload for thatsthem should be treated as pipl
                                  if (clickedAccount.isThatsThemSource) {
                                      platform = 'thatsthem';
                                  }
                                  const isPIPLPlatform = clickedAccount.isThatsThemSource || platform === 'pipl';

                                  const searchTerm = isPIPLPlatform
                                      ? clickedAccount.id
                                      : clickedAccount.userName || clickedAccount.id;
                                  const searchType = MAP_PLATFORM_TO_SEARCH_TYPE[platform] || platform;
                                  const searchDisplayName = isPIPLPlatform
                                      ? formatDisplayName(platform, clickedAccount)
                                      : clickedAccount.userName || clickedAccount.id;
                                  pendoTrack(PENDO_EVENTS.ADD_TO_REPORT_POSSIBLE_MATCHES_ACCOUNT, {
                                      searchTerm,
                                      searchType,
                                      searchDisplayName,
                                      streamId: data.id,
                                      wsId: workspacesData.activeWorkspace.id,
                                      caseId: casesData.activeCaseId,
                                  });

                                  addItemWithExpiry(clickedAccount.id);

                                  const result = await api.collections.addToReport(
                                      workspacesData.activeWorkspace.id,
                                      casesData.activeCaseId,
                                      data.id,
                                      {
                                          searchType,
                                          searchTerm,
                                          searchDisplayName,
                                      },
                                  );

                                  if (isSuccess(result)) {
                                      clickedAccount.added = true;
                                      showMessage(
                                          i18n.translate('ADD_TO_REPORT_NAME_SUCCESS', {
                                              name: clickedAccount.name,
                                          }),
                                          NOTIFICATION.TYPE.SUCCESS,
                                      );
                                  } else {
                                      showMessage(
                                          i18n.translate('ADD_TO_REPORT_NAME_ERROR', {
                                              name: clickedAccount.name,
                                          }),
                                          NOTIFICATION.TYPE.ERROR,
                                      );
                                  }
                                  possibleMatches.updateAddedAccounts([clickedAccount.id]);
                                  setSelectedByPlatform({});
                                  setTimeout(() => fetchPossibleMatches(), constants.POSSIBLE_MATCHES_POLLING_DELAY);
                              },
                              styles: { minWidth: '171px !important', height: 32 },
                          },
                      ]
                    : null
            }
            title={
                clickedAccount ? (
                    <Box sx={{ height: 48, pb: 2 }}>
                        <Link
                            sx={{ fontSize: '16px', fontWeight: '500' }}
                            onClick={() => {
                                setClickedAccount(null);
                            }}
                        >
                            <IconArrowBack style={{ fill: COLORS.BRAND, marginRight: '12px' }} />
                            {`${i18n.translate('Back to')} ${i18n.translate('Possible Matches')}`}
                        </Link>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '964px' }}>
                        {!filterByPlatform && (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="s28w700" sx={{ mr: 1.5 }}>
                                    {data.title}
                                </Typography>
                                <IconPossibleMatches className="fill-bd" style={{ width: '21px', height: '13px' }} />
                            </Box>
                        )}
                        {!filterByPlatform && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: 40,
                                    width: 'calc(100% + 48px)',
                                    backgroundColor: COLORS.BRAND_LIGHTEST,
                                    p: 1,
                                    mt: 2,
                                    mb: 1,
                                }}
                            >
                                <InfoIcon />
                                <Typography sx={{ ml: 1 }}>
                                    {i18n.translate('Enhance your report. Review and add potential matches.')}
                                </Typography>
                            </Box>
                        )}
                        {filterByPlatform && (
                            <Box sx={{ height: 48, pb: 2 }}>
                                <Link
                                    sx={{ fontSize: '16px', fontWeight: '500' }}
                                    onClick={() => {
                                        setFilterByPlatform(null);
                                    }}
                                >
                                    <IconArrowBack style={{ fill: COLORS.BRAND, marginRight: '12px' }} />
                                    {`${i18n.translate('Back to')} ${i18n.translate('Possible Matches')}`}
                                </Link>
                            </Box>
                        )}
                        {possibleMatches.searchInformation && (
                            <MatchesSearchInformation searchInformation={possibleMatches.searchInformation} />
                        )}
                        {possibleMatches.searchInformation && (
                            <Box sx={{ display: 'flex', alignItems: 'baseline', mt: 2, mb: 3 }}>
                                <TextField
                                    type="text"
                                    value={searchInput}
                                    sx={{ width: 371 }}
                                    placeholder={i18n.translate('Find...')}
                                    onChange={(event) => {
                                        possibleMatches.resetAccounts();
                                        setSelectedByPlatform({});
                                        setSearchInput(event.target.value);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <IconSearch className="fill-md" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchInput && (
                                            <InputAdornment
                                                position="start"
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    possibleMatches.resetAccounts();
                                                    setSelectedByPlatform({});
                                                    setSearchInput('');
                                                }}
                                            >
                                                <IconXWindow className="fill-md" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        )}
                        {!possibleMatches.searchInformation && <PossibleMatchesTitleSkeleton />}
                    </Box>
                )
            }
            content={
                clickedAccount ? (
                    <Box>
                        <Box sx={{ mb: 5, display: 'flex' }}>
                            <SmartImage
                                fallbackImg={fallbackUserSquare}
                                src={clickedAccount.profileLocalPicture || clickedAccount.image}
                                containerStyles={{ width: 152, height: 152 }}
                                roundedCorners={true}
                            />
                            <Box sx={{ ml: 3 }}>
                                <Typography variant="s24w500" sx={{ mb: 1 }}>
                                    {clickedAccount.name}
                                </Typography>
                                <ElementsDivider
                                    dividerOverrides={{ height: 16 }}
                                    elements={getInfoLine(clickedAccount)}
                                />
                            </Box>
                        </Box>
                        <Box>
                            {infoBlocks?.leftSections.map((block, index) => (
                                <CustomInfoCard
                                    infoBlocks={[block]}
                                    containerStyles={{ minWidth: '768px', mb: '24px' }}
                                    key={index}
                                />
                            ))}
                        </Box>
                        {!clickedAccount.wideCard && (
                            <Box>
                                <Typography sx={{ mb: 3, mt: 6 }} variant="s28w700">
                                    {i18n.translate('Social Presence')}
                                </Typography>
                                <SocialCard account={clickedAccount} />
                            </Box>
                        )}
                    </Box>
                ) : possibleMatches.accounts ? (
                    <Box sx={{ display: 'block' }}>
                        <Box sx={{ display: 'flex' }}>
                            {showPersonOfInterest && (
                                <Box
                                    sx={{
                                        minWidth: 176,
                                        maxWidth: 176,
                                        height: 'calc(100vh - 325px)',
                                        borderRadius: '8px',
                                        p: 3,
                                        mr: 3,
                                        textAlign: 'center',
                                        position: 'sticky',
                                        top: 0,
                                    }}
                                    className="background-color-mho"
                                >
                                    <Typography className="color-md" sx={{ mb: 1 }}>
                                        {i18n.translate('person of interest')}
                                    </Typography>
                                    <Typography className="color-bd" variant="s18w700" sx={{ mb: 3 }}>
                                        {possibleMatches.overview.name}
                                    </Typography>
                                    <Box
                                        sx={{
                                            overflow: 'auto',
                                            height: 'calc(100vh - 430px)',
                                            width: '100%',
                                            ml: '15px',
                                        }}
                                    >
                                        <CustomList
                                            gap={{ width: 0, height: 3 }}
                                            list={filteredPersonPictures.map((profilePicture, index) => (
                                                <CustomAvatar
                                                    key={index}
                                                    avatarSize={'96px'}
                                                    smallAvatarSize={'48px'}
                                                    avatar={{
                                                        imgLocalSrc: profilePicture.value.imgLocalSrc,
                                                        source:
                                                            profilePicture.sources[0].site ||
                                                            profilePicture.sources[0].source,
                                                    }}
                                                />
                                            ))}
                                        />
                                    </Box>
                                </Box>
                            )}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: `calc(100% - ${showPersonOfInterest ? 200 : 0}px)`,
                                }}
                            >
                                {getSections()}
                                {!!webMentionsFiltered?.length && !filterByPlatform && (
                                    <WebMentions
                                        isPossibleMatches={true}
                                        webMentions={webMentionsFiltered}
                                        styles={{ mt: 2 }}
                                        findInput={searchInput}
                                    />
                                )}
                                {!displayedAccounts && !webMentionsFiltered?.length && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            fontSize: '100px',
                                            justifyContent: 'center',
                                            height: '100%',
                                        }}
                                    >
                                        {<IconNoCollectionsFound />}
                                        <Typography
                                            variant="h2"
                                            sx={{ mt: '27px', fontWeight: '700' }}
                                            className="color-bd"
                                        >
                                            {i18n.translate('No results were found')}
                                        </Typography>
                                        <Typography sx={{ mt: '4px', fontWeight: '500' }} className="color-md">
                                            {i18n.translate('Try modifying your filter criteria or search term')}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        <MatchesActionBar
                            selectedByPlatform={selectedByPlatform}
                            onClickExit={() => {
                                possibleMatches.resetAccounts();
                                setSelectedByPlatform({});
                            }}
                            id={data.id}
                            fetchPossibleMatches={fetchPossibleMatches}
                            possibleMatches={possibleMatches}
                            setSelectedByPlatform={setSelectedByPlatform}
                            addItemWithExpiry={addItemWithExpiry}
                            selectedIds={selectedIds}
                            isButtonDisabled={isButtonDisabled}
                        />
                    </Box>
                ) : null
            }
        />
    );
};

export default PossibleMatchesDialog;
